import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { MDash } from 'ui-components/components/mdash/mdash';

import { useSortedWarehouseLocations } from '../../../use-sorted-warehouse-locations';
import { areProductPropsEqual } from '../utils';
import { messages } from './messages';
import styles from './styles.module.less';
/**
 * @typedef {import('../../../types').ColumnProps} Props
 */

/**
 * @param {Props} props
 * @returns {React.ReactNode}
 */
const Column = ({ value: colProps }) => {
  const { product, warehouseId, updateProduct, showLocationErrors } = colProps;

  const handleLocationChange = useCallback(
    (locationID) => {
      updateProduct(product.rowKey ?? String(product.productId), 'locationId', locationID);
    },
    [product.rowKey, product.productId, updateProduct],
  );

  const locationOptions = useSortedWarehouseLocations(warehouseId);

  const selectedItem = useMemo(
    () => locationOptions.find((item) => item.key === product.locationId),
    [locationOptions, product.locationId],
  );

  if (product.isBundle) {
    return <MDash />;
  }

  const hasError = showLocationErrors && !product.locationId;

  return (
    <DropdownMenu fullWidth hasError={hasError} variant='form'>
      <DropdownMenuToggler className={styles['selected-location']}>
        {selectedItem?.name ?? <FormattedMessage {...messages.locationDropdownDefaultTitle} />}
      </DropdownMenuToggler>
      {locationOptions.map(({ key, name }) => (
        <DropdownMenuItem key={key} onClick={() => handleLocationChange(key)} selected={key === product.locationId}>
          {name}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};

/**
 *
 * @param {Props} prevProps
 * @param {Props} nextProps
 * @returns {boolean}
 */
const arePropsEqual = (prevProps, nextProps) => {
  return (
    areProductPropsEqual('locationId')(prevProps, nextProps) &&
    prevProps.value.warehouseId === nextProps.value.warehouseId &&
    prevProps.value.showLocationErrors === nextProps.value.showLocationErrors
  );
};

export const LocationColumn = memo(Column, arePropsEqual);
